import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useMemo, useState } from "react";
import useVolunteerShopList from "../../../Hooks/useVolunteerShopList";
import { toast } from "sonner";
import { Search, Store, X } from "lucide-react";

export default function AssignVolunteerShopModal({
  isOpen,
  onClose,
  volunteer,
  onSuccess,
}) {
  const {
    volunteerShops,
    loading,
    fetchVolunteerShops,
    assignShopToVolunteer,
  } = useVolunteerShopList();

  const [selectedShopId, setSelectedShopId] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchVolunteerShops();
      setSelectedShopId("");
      setSearch("");
    }
  }, [isOpen]);

  const filteredShops = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return volunteerShops || [];

    return (volunteerShops || []).filter((shop) =>
      String(shop.shop_name || "").toLowerCase().includes(q),
    );
  }, [volunteerShops, search]);

  const handleAssign = async () => {
    if (!selectedShopId) {
      toast.error("Please select a shop");
      return;
    }

    const success = await assignShopToVolunteer(
      volunteer.volunteer_code,
      selectedShopId,
    );

    if (success) {
      onSuccess?.();
      onClose?.();
    }
  };

  if (!isOpen || !volunteer) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[2px]" />

        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 bg-white shadow-2xl outline-none">
          {/* Header */}
          <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-slate-900">
                Assign Shop
              </Dialog.Title>
              <p className="mt-1 text-sm text-slate-500">
                Select a shop for{" "}
                <span className="font-medium text-slate-700">
                  {volunteer.first_name}
                </span>
              </p>
            </div>

            <button
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            {/* Search */}
            <div className="relative mb-4">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search shop name..."
                className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </div>

            {/* Shop List */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
              <div className="max-h-[320px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center px-4 py-10 text-sm text-slate-500">
                    Loading shops...
                  </div>
                ) : filteredShops?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                      <Store size={20} />
                    </div>
                    <p className="text-sm font-medium text-slate-700">
                      No shops found
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Try another search term
                    </p>
                  </div>
                ) : (
                  filteredShops.map((shop) => {
                    const shopId = shop.shop_id ?? shop.id;
                    const isSelected = selectedShopId === shopId;

                    return (
                      <button
                        key={shopId}
                        type="button"
                        onClick={() => setSelectedShopId(shopId)}
                        className={`flex w-full items-center justify-between border-b border-slate-200 px-4 py-3 text-left transition last:border-b-0 ${
                          isSelected
                            ? "bg-blue-50 ring-1 ring-inset ring-blue-200"
                            : "bg-white hover:bg-slate-50"
                        }`}
                      >
                        <div className="min-w-0">
                          <p
                            className={`truncate text-sm font-medium ${
                              isSelected ? "text-blue-700" : "text-slate-800"
                            }`}
                          >
                            {shop.shop_name}
                          </p>

                          {shop.shop_code && (
                            <p className="mt-0.5 text-xs text-slate-500">
                              Code: {shop.shop_code}
                            </p>
                          )}
                        </div>

                        <div
                          className={`ml-3 h-4 w-4 rounded-full border-2 ${
                            isSelected
                              ? "border-blue-600 bg-blue-600"
                              : "border-slate-300 bg-white"
                          }`}
                        >
                          {isSelected && (
                            <div className="flex h-full w-full items-center justify-center">
                              <div className="h-1.5 w-1.5 rounded-full bg-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
            <button
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              onClick={handleAssign}
              disabled={!selectedShopId || loading}
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Please wait..." : "Assign Shop"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}